import { useEffect, useRef, useState, type RefObject } from 'react';
import { useApp } from '../store';
import { displayName, initials } from '../people';
import VendorTag from './VendorTag';

/**
 * THE SIGN-OFF COVER PICKER AND ITS DATE HELPERS — shared by the person's own
 * "When I'm away" screen (WhenAway) and the admin's "arrange cover for someone"
 * panel (PersonAccess). One component so both read and behave identically: the
 * app-standard `.sr-ta-*` typeahead, Escape/Cancel/click-away to close, and the
 * roster minus the person being covered.
 */

/** A timestamp (ms) → 'YYYY-MM-DD' in local time, the day DateField reads. */
export function dayFromMs(ms?: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 'YYYY-MM-DD' → the ms at the END of that local day, so cover lasts the whole
    chosen day rather than expiring at its first second. '' clears the end. */
export function endOfDayMs(iso: string): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

/**
 * A plain teammate typeahead. Cover is for people already in the workspace (they
 * sign as themselves), so there is no "invite by email" arm the sign-off setup
 * typeahead has. Closes on Escape, Cancel, or a click away.
 */
export default function CoverPicker({
  me,
  label = 'Who signs for me',
  emails,
  profiles,
  busy,
  onPick,
  onCancel,
  boundary,
}: {
  /** The person being covered — excluded from the list (nobody covers themselves). */
  me: string;
  /** The field label, e.g. "Who signs for me" or "Who signs for Pat". */
  label?: string;
  emails: string[];
  profiles: ReturnType<typeof useApp.getState>['profiles'];
  busy: boolean;
  onPick: (email: string) => void;
  onCancel: () => void;
  /** What counts as "inside" for click-away. Defaults to the picker panel — right
      when the picker replaces the card (WhenAway). When the picker is embedded in a
      larger form that has OTHER controls (the admin arrange form's Reason field),
      pass that form's ref, or clicking a sibling field reads as a click-away and
      cancels the whole form — a real bug a user walk caught. */
  boundary?: RefObject<HTMLElement | null>;
}) {
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /* Grab focus only when the picker stands alone (WhenAway). When embedded in a
       form with its own first field (the admin arrange form's autofocused Reason),
       leave focus to that field rather than stealing it to the search box. */
    if (!boundary) input.current?.focus();
  }, [boundary]);
  /* Close on a click away, like every other menu/popover in the app
     ([[feedback-menus-close-on-click-away]]). The picker replaces the card while
     open, so there is no opener to keep — testing the panel alone is enough.
     Escape and Cancel are the keyboard/explicit ways out. */
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const scope = boundary?.current ?? panel.current;
      if (!scope?.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [onCancel, boundary]);

  const needle = q.trim().toLowerCase();
  const people = [...new Set(emails.map((e) => e.toLowerCase()))]
    .filter((e) => e !== me)
    .sort()
    .map((e) => ({ email: e, name: displayName(e, profiles), position: profiles[e]?.title?.trim() ?? '' }))
    .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.email.includes(needle))
    .slice(0, 8);

  return (
    <div className="cover-picker" ref={panel}>
      <p className="cover-lbl">
        {label} <span className="req">*</span>
      </p>
      <input
        ref={input}
        className="input sr-ta-input"
        placeholder="Search a teammate by name or email…"
        aria-required="true"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && people.length) {
            e.preventDefault();
            onPick(people[0].email);
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
      />
      <div className="sr-ta-body cover-picker-body">
        {people.length === 0 ? (
          <p className="sr-ta-empty">
            No teammates match. Cover is for people already in this workspace.
          </p>
        ) : (
          people.map((p) => (
            <button
              className="sr-ta-opt"
              key={p.email}
              disabled={busy}
              onClick={() => onPick(p.email)}
            >
              <span className="sr-ta-av" aria-hidden="true">
                {initials(p.email, profiles)}
              </span>
              <span className="sr-ta-opt-text">
                <span className="sr-ta-nameline"><span className="sr-ta-name">{p.name}</span><VendorTag who={p.email} profiles={profiles} compact /></span>
                {p.position && <span className="sr-ta-sub">{p.position}</span>}
              </span>
            </button>
          ))
        )}
      </div>
      <div className="cover-picker-foot">
        <button className="btn btn-small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
